import * as React from "react";

import { Menu, styled, ImageListItemBar } from "@mui/material";
import {
  Button,
  Typography,
  Grid,
  Skeleton,
  MenuItem,
  Box,
} from "mpulse-design-system";
import { ArrowDropDown } from "@mui/icons-material";

import FieldChoiceService from "../../services/FieldChoice/FieldChoiceService";

const FieldChoiceOptionsMenu = ({
  i,
  field,
  error,
  fieldId,
  accountId,
  handleMetadataChange,
  isNew,
  isViewMode,
  isEditMode,
  value,
  reset,
  addAnotherMetadata,
}) => {
  const selectAnOption = "Select an Option";
  const selectText = value || selectAnOption;
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [fieldChoiceOptions, setFieldChoiceOptions] = React.useState([]);
  const [selectedOption, setSelectedOption] = React.useState(selectText);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleChooseOption = (e) => {
    handleMetadataChange({
      target: {
        name: field.name,
        value: e.currentTarget.id || e.target.id,
      },
    });
    setSelectedOption(e.currentTarget.dataset.value);
    handleClose();
  };

  React.useEffect(() => {
    if (!addAnotherMetadata && (isNew || isEditMode)) {
      FieldChoiceService.getFieldChoices(accountId, fieldId).then((data) => {
        setFieldChoiceOptions(data);
      });
    } else if (isNew && addAnotherMetadata) {
      setSelectedOption(selectAnOption);
    }
  }, [isNew, isEditMode, reset]);

  return (
    <Grid key={`field-${i}`} item container xxs={12} spacing={0.5}>
      <Grid item xxs={12}>
        <Typography color="textSecondary" variant="overline">
          {field.label}:
        </Typography>
      </Grid>
      <Grid item xxs={12}>
        {fieldChoiceOptions.length >= 1 || isViewMode ? (
          <>
            <StyledButton
              id={`${field.name}-button`}
              aria-controls={open ? `${field.name}-menu` : undefined}
              aria-haspopup="true"
              aria-expanded={open ? "true" : undefined}
              variant="outlined"
              disableElevation
              color={error ? "error" : "inherit"}
              size="large"
              onClick={handleClick}
              endIcon={isViewMode ? null : <ArrowDropDown />}
              sx={{
                width: "100%",
                paddingLeft: (theme) => theme.spacing(2),
                pointerEvents: isViewMode ? "none" : "all",
                borderColor: error ? "#C01548" : null,
              }}
            >
              <Typography
                color="textPrimary"
                variant="body1"
                sx={{
                  opacity: selectedOption === selectAnOption ? ".5" : "1",
                  color: "#3B4F69 !important",
                }}
              >
                {selectedOption}
              </Typography>
            </StyledButton>
            {field.type.startsWith("FIELD_CHOICE_IMAGE") ? (
              <ImgOptions
                {...{
                  field,
                  anchorEl,
                  handleClose,
                  open,
                  handleChooseOption,
                  fieldChoiceOptions,
                  selectedOption,
                }}
              />
            ) : (
              <TextOptions
                {...{
                  field,
                  anchorEl,
                  handleClose,
                  open,
                  handleChooseOption,
                  fieldChoiceOptions,
                  selectedOption,
                }}
              />
            )}
          </>
        ) : (
          <Skeleton sx={{ height: "40px" }} />
        )}
      </Grid>
      {(isNew || isEditMode) && field.required_for_production ? (
        <Grid item xxs={12}>
          <Typography
            color={error ? "#C01548" : "textTertiary"}
            variant="body2"
          >
            Required
          </Typography>
        </Grid>
      ) : null}
    </Grid>
  );
};

const TextOptions = ({
  field,
  anchorEl,
  handleClose,
  open,
  handleChooseOption,
  fieldChoiceOptions,
  selectedOption,
}) => {
  return (
    <Menu
      id={`${field.name}-menu`}
      MenuListProps={{
        "aria-labelledby": `${field.name}-button`,
      }}
      anchorEl={anchorEl}
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          width: anchorEl && anchorEl.offsetWidth,
          ".MuiList-root": {
            padding: 0,
          },
          "& .MuiMenuItem-root": {
            height: "40px",
            "&:hover": {
              "& .MuiTypography-root": {
                color: (theme) => `${theme.palette.primary.light} !important`,
              },
            },
          },
        },
      }}
    >
      {fieldChoiceOptions.map((item, i) => {
        return (
          <MenuItem
            key={`menu-item-${i}`}
            onClick={handleChooseOption}
            id={item.id}
            data-value={item.option.value}
            autoFocus={selectedOption === item.option.value}
          >
            <Typography key={`item-${i}`} color="textPrimary" variant="body1">
              {item.option.value}
            </Typography>
          </MenuItem>
        );
      })}
    </Menu>
  );
};

const ImgOptions = ({
  field,
  anchorEl,
  handleClose,
  open,
  handleChooseOption,
  fieldChoiceOptions,
  selectedOption,
}) => {
  return (
    <Menu
      id={`${field.name}-menu`}
      MenuListProps={{
        "aria-labelledby": `${field.name}-button`,
      }}
      anchorEl={anchorEl}
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          width: anchorEl && anchorEl.offsetWidth,
          ".MuiList-root": {
            padding: 0,
          },
          "& .MuiMenuItem-root": {
            "&:hover": {
              "& .MuiTypography-root": {
                color: (theme) => `${theme.palette.primary.light} !important`,
              },
            },
          },
        },
      }}
    >
      {fieldChoiceOptions.map((item, i) => {
        return (
          <MenuItem
            key={`menu-item-${i}`}
            id={item.id}
            data-value={item.option.value}
            onClick={handleChooseOption}
            autoFocus={selectedOption === item.option.value}
          >
            <Grid container spacing={2}>
              <Grid item xs>
                {item.option.file ? (
                  <img
                    width="162px"
                    style={{ flexGrow: "1" }}
                    key={`menu-item-img-${i}`}
                    id={item.id}
                    data-value={item.option.value}
                    src={item.option.file}
                    alt={item.option.value}
                    loading="lazy"
                  />
                ) : (
                  <Box sx={{ width: 162 }} />
                )}
              </Grid>
              <Grid item xs>
                <ImageListItemBar
                  sx={{ flexGrow: "3" }}
                  key={`menu-item-img-label-${i}`}
                  id={item.id}
                  data-value={item.option.value}
                  title={item.option.value}
                  position="below"
                />
              </Grid>
            </Grid>
          </MenuItem>
        );
      })}
    </Menu>
  );
};

const StyledButton = styled(Button)(({ theme }) => ({
  justifyContent: "space-between",
  width: "150px",
  height: "40px",
  borderColor: theme.palette.grey[300],
  color: theme.palette.grey[550],
  letterSpacing: 0,
  textTransform: "unset !important",
  ...theme.typography.body1,
}));

export default FieldChoiceOptionsMenu;
